import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Calendar, Users } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { ClosureImpactedReservation } from "@/lib/closureImpactUtils";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ClosureImpactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  impactedReservations: ClosureImpactedReservation[];
  closurePeriod: { startDate: string; endDate: string; reason?: string };
  loading?: boolean;
  isEditing?: boolean;
}

export function ClosureImpactDialog({
  open,
  onOpenChange,
  onConfirm,
  impactedReservations,
  closurePeriod,
  loading = false,
  isEditing = false,
}: ClosureImpactDialogProps) {
  const startDate = new Date(closurePeriod.startDate);
  const endDate = new Date(closurePeriod.endDate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            Réservations impactées
          </DialogTitle>
          <DialogDescription>
            La période de fermeture du {format(startDate, "d MMMM", { locale: fr })} au{" "}
            {format(endDate, "d MMMM yyyy", { locale: fr })} impacte des réservations existantes.
          </DialogDescription>
        </DialogHeader>

        <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <AlertDescription className="text-destructive font-medium">
            <strong>{impactedReservations.length} réservation{impactedReservations.length > 1 ? 's' : ''}</strong>{" "}
            {impactedReservations.length > 1 ? 'sont programmées' : 'est programmée'} pendant cette période.
            <span className="block mt-1 font-bold">
              ⚠️ Ces réservations ne seront pas automatiquement annulées. Vous devez les gérer manuellement.
            </span>
          </AlertDescription>
        </Alert>

        <ScrollArea className="max-h-64">
          <div className="space-y-2">
            {impactedReservations.map((reservation) => (
              <div
                key={reservation.id}
                className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">{reservation.customerName}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(reservation.date), "EEEE d MMMM", { locale: fr })} à{" "}
                      {reservation.startTime.substring(0, 5)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">{reservation.numberOfPeople}</span>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Annuler
          </Button>
          <Button 
            variant="default" 
            onClick={onConfirm} 
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {loading ? "En cours..." : isEditing ? "Modifier quand même" : "Créer quand même"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
